/** @typedef {'name' | 'modified' | 'size' | 'type'} StorageBoxSortField */
/** @typedef {'asc' | 'desc'} StorageBoxSortDir */

const SORT_FIELDS = new Set(['name', 'modified', 'size', 'type']);
const SORT_DIRS = new Set(['asc', 'desc']);

export function normalizeSortField(value) {
  return SORT_FIELDS.has(value) ? value : 'name';
}

export function normalizeSortDir(value) {
  return SORT_DIRS.has(value) ? value : 'asc';
}

function folderRank(item) {
  return item?.type === 'folder' ? 0 : 1;
}

function compareName(a, b) {
  return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
}

/**
 * @param {Array<{ type?: string, name?: string, updatedAt?: string, createdAt?: string, sizeBytes?: number }>} items
 * @param {StorageBoxSortField} sortField
 * @param {StorageBoxSortDir} sortDir
 */
export function sortStorageBoxItems(items, sortField, sortDir) {
  if (!items?.length) return [];
  const field = normalizeSortField(sortField);
  const dir = normalizeSortDir(sortDir);
  const sign = dir === 'desc' ? -1 : 1;

  return [...items].sort((a, b) => {
    if (field !== 'type') {
      const folderCmp = folderRank(a) - folderRank(b);
      if (folderCmp !== 0) return folderCmp;
    }

    let cmp = 0;
    switch (field) {
      case 'name':
        cmp = compareName(a, b);
        break;
      case 'modified': {
        const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
        cmp = ta - tb;
        break;
      }
      case 'size':
        cmp = (a.sizeBytes || 0) - (b.sizeBytes || 0);
        break;
      case 'type': {
        cmp = folderRank(a) - folderRank(b);
        if (cmp === 0) cmp = compareName(a, b);
        break;
      }
      default:
        cmp = 0;
    }

    if (cmp === 0) return compareName(a, b);
    return cmp * sign;
  });
}

export const STORAGE_BOX_SORT_OPTIONS = [
  { field: 'name', dir: 'asc', label: 'Name (A → Z)' },
  { field: 'name', dir: 'desc', label: 'Name (Z → A)' },
  { field: 'modified', dir: 'desc', label: 'Modified (newest first)' },
  { field: 'modified', dir: 'asc', label: 'Modified (oldest first)' },
  { field: 'size', dir: 'desc', label: 'Size (largest first)' },
  { field: 'size', dir: 'asc', label: 'Size (smallest first)' },
  { field: 'type', dir: 'asc', label: 'Folders first' },
  { field: 'type', dir: 'desc', label: 'Files first' },
];

export function sortOptionValue(field, dir) {
  return `${normalizeSortField(field)}:${normalizeSortDir(dir)}`;
}

export function parseSortOptionValue(value) {
  const [field, dir] = String(value || '').split(':');
  return {
    sortField: normalizeSortField(field),
    sortDir: normalizeSortDir(dir),
  };
}
